import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { useLocation, useHistory } from "react-router-dom";
import { makeApi, URLS } from "Utils/apiURL";
import deepResearchStore from "Stores/deepResearchData";
import copilotStore from "Stores/copilotStore";
import copilotDrawerStore from "Stores/copilotDrawerStore";
import { useCopilotContext } from "Pages/Dashboard/Copilot/agent/copilotContext";
import usePageFindings from "Pages/Dashboard/Copilot/agent/usePageFindings";
import deepResearchFindings from "./deepResearchFindings";
import ConversationStarters from "./components/ConversationStarters";
import SellingSignals from "./components/SellingSignals";
import ProfileSummary from "./components/ProfileSummary";
import DeeperContext from "./components/DeeperContext";
import CareerTimeline from "./components/CareerTimeline";
import BestChannel from "./components/BestChannel";
import WarmIntros from "./components/WarmIntros";
import OrgChart from "./components/OrgChart";
import KeyMentions from "./components/KeyMentions";
import CompanyOverview from "./components/CompanyOverview";
import ResearchSourcesPanel from "./components/ResearchSourcesPanel";
import ResearchHistoryStrip from "./components/ResearchHistoryStrip";
import ResearchingPlaceholder from "./components/ResearchingPlaceholder";
import InsightsLoader from "./components/InsightsLoader";
import { ContactHeroCard, WhyThisContact } from "./components/ContactHeroCard";
import ChatPanel from "./components/ChatPanel";
import SellerProfileBar from "./components/SellerProfileBar";
import SellerProfileSetup from "./components/SellerProfileSetup";
import RerunResearchPopup from "./components/RerunResearchPopup";
import { toasterState } from "Components/common/toaster";
import "./deepResearch.scss";

const LOADING_MESSAGES = [
  { text: "🔎 Searching the web for {{name}}'s public profiles…" },
  { text: "📇 Mapping social handles and digital footprint..." },
  { text: "🎯 Highlighting major milestones, recognitions, and achievements..." },
  { text: "🧠 Analyzing {{name}}'s LinkedIn to decode thought leadership and content patterns..." },
  { text: "📚 Reading through {{name}}'s career journey, company transitions, and leadership roles..." },
  { text: "🎙️ Looking for mentions in podcasts, events, and interviews..." },
  { text: "🖇️ Scanning for exclusive community memberships and networks..." },
  { text: "💼 Reviewing {{name}}'s company's market position, product focus, and traction..." },
  { text: "🌐 Searching for recent mentions in news, blogs, and startup forums..." },
  { text: "📊 Generating high-value talking points and insights for your outreach..." },
  { text: "✅ Almost there… finalizing your personalized deep research summary!" }
];

// ── Left Section: Progress panel with animated loading messages ──
const LeftSection = observer(({ loadingList, contactName, isComplete, timer }) => {
  const lastRef = useCallback((el) => {
    if (el) el.scrollIntoView({ behavior: "smooth" });
  }, [loadingList.length]);

  return (
    <div className="drLeftPanel">
      <div className="drLeftProfile">
        <div className="drLeftName">{contactName}</div>
      </div>
      <div className="drLeftProgress">
        {!isComplete && (
          <div className="drLeftHeader">
            <span className="drLeftDots">●●●</span>
            <div className="drLeftTitleWrap">
              <p className="drLeftTitle">Deep Research</p>
              <span className="drLeftTimer">{timer.toFixed(1)} seconds</span>
            </div>
          </div>
        )}
        {isComplete && (
          <div className="drLeftCompleted">
            <span className="drLeftCompletedIcon">✓</span>
            <p>Deep Research Completed</p>
            <span className="drLeftCompletedTime"><b>{timer.toFixed(1)}</b> seconds</span>
          </div>
        )}
        <div className="drLeftList">
          <div className="drLeftListInner">
            {loadingList.map((item, index) => {
              const isLast = index === loadingList.length - 1;
              return (
                <div className="drLeftListItem" key={index} ref={isLast ? lastRef : null}>
                  <span className={`drLeftItemIcon ${isLast && !isComplete ? "spinner" : "tick"}`}>
                    {isLast && !isComplete ? "⟳" : "✓"}
                  </span>
                  <p>{item}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
});

const DeepResearch = observer(({ contactData = {} }) => {
  const location = useLocation();
  const history = useHistory();
  const queryParams = new URLSearchParams(location?.search || "");

  const linkedInUrl = contactData?.linkedInUrl || contactData?.linkedin_url
    || queryParams.get("linkedInUrl") || deepResearchStore.currentLinkedInUrl || "";
  const contactName = contactData?.firstName
    ? `${contactData.firstName} ${contactData.lastName || ""}`.trim()
    : queryParams.get("contactName") || deepResearchStore.currentContactName || "";

  const [showSellerPopup, setShowSellerPopup] = useState(false);
  const [showRerunPopup, setShowRerunPopup] = useState(false);
  const [loadingList, setLoadingList] = useState([]);
  const [timer, setTimer] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const [researchStarted, setResearchStarted] = useState(false);
  const [overviewChecked, setOverviewChecked] = useState(false);
  // Suppression flag for the contact/seller-change effect below. When the
  // popup-Run flow sets activeSellerProfileId and immediately calls
  // startResearch(), the effect would otherwise re-fire on the seller-id
  // change and wipe the in-progress research state. We flip this true right
  // before the popup-driven start, and the effect honors it for one tick.
  const skipNextSellerSwitchResetRef = useRef(false);

  // Normalize URL
  const normalizedUrl = useMemo(() => {
    let url = linkedInUrl;
    if (!url.startsWith("http")) url = "https://" + url;
    if (!url.includes("linkedin.com")) url = "https://www.linkedin.com/in/" + url;
    return url;
  }, [linkedInUrl]);

  const msgInterval = 4000;

  // ── Real-time progress channel ────────────────────────────────────
  // Tries SSE first; falls back to polling if EventSource errors (older
  // browsers, restrictive proxies). Both paths funnel the same event shape
  // into deepResearchStore.applyProgressEvent — UI is wired off the store.
  useEffect(() => {
    const cId = contactData?.contactId;
    const alreadyComplete = deepResearchStore.loadingPhase === "complete";
    if (!cId || !researchStarted || alreadyComplete) return undefined;

    let cancelled = false;
    let es = null;
    let pollTimer = null;
    let cursor = 0;

    const drainEvents = (events) => {
      if (!Array.isArray(events)) return;
      for (const evt of events) {
        if (!evt) continue;
        deepResearchStore.applyProgressEvent(evt);
        if (typeof evt.ts === "number" && evt.ts > cursor) cursor = evt.ts;
      }
    };

    const startPolling = () => {
      const tick = () => {
        if (cancelled) return;
        makeApi({
          url: `${URLS.deepResearchProgress}/${cId}?since=${cursor}`,
          method: "GET"
        }).then(res => {
          if (cancelled) return;
          const data = res?.data;
          if (data) {
            drainEvents(data.events);
            if (data.done) return; // stop polling
          }
          pollTimer = setTimeout(tick, 1500);
        }).catch(() => {
          if (!cancelled) pollTimer = setTimeout(tick, 3000);
        });
      };
      tick();
    };

    // Try SSE first.
    if (typeof window !== "undefined" && "EventSource" in window) {
      try {
        es = new EventSource(`${URLS.deepResearchStream}/${cId}`, { withCredentials: true });
        es.onmessage = (e) => {
          // Default channel — only fires for events without a `name`. Our BE
          // names each event by type, so this rarely triggers, but handle it
          // for safety.
          try { deepResearchStore.applyProgressEvent(JSON.parse(e.data)); } catch (_) {}
        };
        const handler = (e) => {
          try { deepResearchStore.applyProgressEvent(JSON.parse(e.data)); } catch (_) {}
        };
        [
          "research_started", "serp_bucket_done", "triage_done",
          "page_crawled", "page_extracted", "synthesis_done", "research_failed"
        ].forEach(t => es.addEventListener(t, handler));

        es.onerror = () => {
          // Connection died — close and fall back to polling for the
          // remainder of this research run.
          if (es) { es.close(); es = null; }
          if (!cancelled && !deepResearchStore.loadingPhase.includes("complete")) {
            startPolling();
          }
        };
      } catch (e) {
        startPolling();
      }
    } else {
      startPolling();
    }

    return () => {
      cancelled = true;
      if (es) es.close();
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [contactData?.contactId, researchStarted, deepResearchStore.loadingPhase]);


  useEffect(() => {
    deepResearchStore.fetchSellerProfiles();
  }, []);

  // Timer
  useEffect(() => {
    if (!timerActive) return;
    const interval = setInterval(() => setTimer(prev => prev + 0.1), 100);
    return () => clearInterval(interval);
  }, [timerActive]);

  // Loading messages
  useEffect(() => {
    if (!researchStarted) return;
    const interval = setInterval(() => {
      setLoadingList(prev => {
        if (prev.length < LOADING_MESSAGES.length) {
          const msg = LOADING_MESSAGES[prev.length].text.replace(/\{\{name\}\}/g, contactName || "prospect");
          return [...prev, msg];
        }
        clearInterval(interval);
        return prev;
      });
    }, msgInterval);
    return () => clearInterval(interval);
  }, [researchStarted, contactName]);

  // Start research
  //
  // The old flow fired two extra SERP calls (/userSearch + /userCompanySearch)
  // before /userProfile purely for the perceived-speed "links visible in 3s"
  // effect. That cost +$0.002/research and returned data already covered by
  // the 15-bucket fan-out inside /userProfile. We now drop both — the
  // bucketed Sources panel streams results in real-time via the progress
  // channel, and ResearchingPlaceholder fills the visual gap until the first
  // bucket arrives.
  const startResearch = useCallback(() => {
    if (!linkedInUrl) return;
    setResearchStarted(true);
    setTimerActive(true);
    deepResearchStore.reset();
    deepResearchStore.setLoading(true, "researching");

    const activeProfile = deepResearchStore.activeSellerProfile;
    makeApi({
      url: URLS.deepResearchUserProfile,
      method: "POST",
      data: {
        linked_in_url: normalizedUrl,
        contactId: contactData?.contactId || "",
        seller_profile: activeProfile || {},
        seller_profile_id: activeProfile?.id || "",
        model: "instant"
      }
    }).then(response => {
      if (response?.data) {
        deepResearchStore.setProfileResult(response.data);
        deepResearchStore.setLoading(false, "complete");
      } else {
        deepResearchStore.setError("No data returned");
      }
      setTimerActive(false);
    }).catch(err => {
      deepResearchStore.setError(err?.message || "Research failed");
      setTimerActive(false);
    });
  }, [linkedInUrl, normalizedUrl, contactName]);

  // Reset and re-fetch whenever the contact changes OR the active seller
  // profile changes (e.g. switching sellers in the dropdown to view that
  // seller's research run for the same contact).
  //
  // Without the seller dependency, switching the dropdown would silently keep
  // showing the previously-loaded seller's data — confusing AND in the case of
  // a sequence-bound run it'd send the rep into chat with the wrong context.
  useEffect(() => {
    const cId = contactData?.contactId;
    if (!cId) {
      setOverviewChecked(true);
      return;
    }

    // Popup-Run flow sets activeSellerProfileId then immediately fires
    // startResearch(). The seller-id change re-enters this effect — without
    // this guard it would wipe the in-flight research state and the user
    // would see the landing page reappear mid-research. Consume the flag and
    // skip the reset+refetch for this one tick.
    if (skipNextSellerSwitchResetRef.current) {
      skipNextSellerSwitchResetRef.current = false;
      setOverviewChecked(true);
      return;
    }

    // Wipe state from any previously-loaded contact / seller.
    deepResearchStore.reset();
    setResearchStarted(false);
    setOverviewChecked(false);
    setLoadingList([]);
    setTimer(0);
    setTimerActive(false);

    let cancelled = false;
    // Pin the overview lookup to the currently-selected seller so the rep sees
    // *that seller's* run, not whichever happens to be newest in Mongo. When
    // no seller is selected yet (initial mount before seller profiles load) we
    // omit the param and the BE falls back to latest-by-date.
    const activeSeller = deepResearchStore.activeSellerProfileId;
    const url = activeSeller
      ? `${URLS.deepResearchOverview}/${cId}?sellerProfileId=${encodeURIComponent(activeSeller)}`
      : `${URLS.deepResearchOverview}/${cId}`;
    makeApi({ url, method: "GET" }).then(res => {
      if (cancelled) return;
      if (res?.data && Object.keys(res.data).length > 0) {
        deepResearchStore.setProfileResult(res.data);
        deepResearchStore.setLoading(false, "complete");
        setResearchStarted(true);
      }
      setOverviewChecked(true);
    }).catch(() => {
      if (!cancelled) setOverviewChecked(true);
    });

    return () => { cancelled = true; };
  }, [contactData?.contactId, deepResearchStore.activeSellerProfileId]);

  // Gate auto-start on overview-check + seller profiles loaded.
  //  - If existing data was loaded from DB (hasData), do nothing — legacy rows
  //    without a seller_profile_id intentionally leave the dropdown unselected.
  //  - If no seller profiles exist yet, force the setup popup to create one
  //    (user can't proceed without at least one).
  //  - If exactly one profile exists, auto-pick + auto-start (no choice to make).
  //  - If multiple profiles exist, do NOT auto-pick or auto-start — let the
  //    user explicitly choose via SellerProfileBar before clicking the start
  //    button. Auto-picking the first one silently was confusing (the rep
  //    didn't realize a different profile would have changed the research).
  useEffect(() => {
    if (!overviewChecked) return;
    if (!deepResearchStore.sellerProfilesLoaded) return;
    if (!linkedInUrl) return;
    if (researchStarted || deepResearchStore.hasData) return;

    if (!deepResearchStore.sellerProfiles.length) {
      setShowSellerPopup(true);
      return;
    }
    if (deepResearchStore.sellerProfiles.length === 1) {
      // Only one profile — no choice to make, auto-pick and start.
      if (!deepResearchStore.activeSellerProfileId) {
        deepResearchStore.setActiveSellerProfileId(deepResearchStore.sellerProfiles[0].id);
        return;
      }
      startResearch();
      return;
    }
    // Multiple profiles — wait for explicit selection. The landing UI shows
    // the SellerProfileBar so the rep can pick before clicking Start.
  }, [overviewChecked, deepResearchStore.sellerProfilesLoaded, deepResearchStore.sellerProfiles.length, deepResearchStore.activeSellerProfileId, linkedInUrl, researchStarted]);

  const { isLoading, loadingPhase, hasData } = deepResearchStore;
  const isComplete = loadingPhase === "complete";

  useCopilotContext("deep_research", () => ({
    entity: deepResearchStore.currentContactId ? { type: "contact", id: deepResearchStore.currentContactId } : undefined,
    viewState: { phase: deepResearchStore.loadingPhase }
  }));

  const copilotFindings = useMemo(
    () =>
      deepResearchFindings({
        sellerProfileId: deepResearchStore.activeSellerProfileId,
        starters: deepResearchStore.conversationStarters,
        contactId: deepResearchStore.currentContactId,
        phase: deepResearchStore.loadingPhase,
        actions: {
          createProfile: () => history.push("/settings/seller-profiles/new"),
          buildSequence: () => {
            copilotStore.setPendingAutoSend(
              "Draft a 3-step sequence for this contact using the research and conversation starters. Show me before creating."
            );
            copilotDrawerStore.openFor("deep_research", "");
          },
          callScript: () => {
            copilotStore.setPendingAutoSend(
              "Write a call script for this contact from the research: 30-second opener, two discovery questions, and the likely objection with a reply."
            );
            copilotDrawerStore.openFor("deep_research", "");
          }
        }
      }),
    [
      deepResearchStore.activeSellerProfileId,
      deepResearchStore.conversationStarters,
      deepResearchStore.currentContactId,
      deepResearchStore.loadingPhase
    ]
  );
  usePageFindings(copilotFindings, "deep_research");

  // ── Initial loading state — wait until we know whether existing research exists ──
  if (!overviewChecked || !deepResearchStore.sellerProfilesLoaded) {
    return (
      <div className="deepResearchContainer">
        <div className="drInitialLoading">
          <div className="drInitialLoadingSpinner" />
          <p>Loading deep research…</p>
        </div>
      </div>
    );
  }

  // ── Landing state ──
  if (!researchStarted && !hasData) {
    return (
      <div className="deepResearchContainer">
        {showSellerPopup && (
          <SellerProfileSetup
            existingProfile={deepResearchStore.activeSellerProfile}
            onClose={() => setShowSellerPopup(false)}
            onSave={(profile) => {
              const editingId = deepResearchStore.activeSellerProfileId;
              const payload = editingId ? { ...profile, id: editingId } : profile;
              deepResearchStore.saveSellerProfile(payload);
              setShowSellerPopup(false);
            }}
          />
        )}
        {showRerunPopup && (
          <RerunResearchPopup
            profiles={deepResearchStore.sellerProfiles}
            initialSelectedId={deepResearchStore.activeSellerProfileId || ""}
            title="Choose a seller profile"
            runButtonLabel="Start research"
            onClose={() => setShowRerunPopup(false)}
            onAddNew={() => {
              setShowRerunPopup(false);
              deepResearchStore.setActiveSellerProfileId(null);
              setShowSellerPopup(true);
            }}
            onRun={(sellerId) => {
              // Tell the contact/seller-change effect to skip its reset
              // pass on this seller-id change — startResearch() below sets
              // the in-flight state and the effect's reset would otherwise
              // immediately undo it (landing reappears mid-research).
              skipNextSellerSwitchResetRef.current = true;
              deepResearchStore.setActiveSellerProfileId(sellerId);
              setLoadingList([]);
              setTimer(0);
              setShowRerunPopup(false);
              startResearch();
            }}
          />
        )}
        <SellerProfileBar
          profiles={deepResearchStore.sellerProfiles}
          activeProfileId={deepResearchStore.activeSellerProfileId}
          loadedResearchProfileId={deepResearchStore.loadedResearchSellerProfileId}
          onSelectProfile={(id) => deepResearchStore.setActiveSellerProfileId(id)}
          onAddNewProfile={() => {
            deepResearchStore.setActiveSellerProfileId(null);
            setShowSellerPopup(true);
          }}
          onEditProfile={() => setShowSellerPopup(true)}
        />
        <div className="drLanding">
          <i className="material-icons-outlined drIcon" style={{ fontSize: "48px" }}>biotech</i>
          <h2 className="drLandingTitle">Deep Research</h2>
          {contactName && (
            <p className="drLandingSubtitle">
              Discover what makes <strong>{contactName}</strong> tick
            </p>
          )}
          <div className="drLandingFeatures">
            <div className="drFeatureItem"><span>💬</span><span>Conversation starters that actually get replies</span></div>
            <div className="drFeatureItem"><span>📈</span><span>Selling signals with urgency scoring</span></div>
            <div className="drFeatureItem"><span>🏢</span><span>Company intel — competitors, tech stack, hiring</span></div>
            <div className="drFeatureItem"><span>⏰</span><span>Timing signals — why reach out right now</span></div>
          </div>
          <button
            className="drStartBtn"
            onClick={() => {
              if (!linkedInUrl) return;
              if (!deepResearchStore.sellerProfiles.length) {
                // No profiles → must create one before researching.
                setShowSellerPopup(true);
                return;
              }
              if (!deepResearchStore.activeSellerProfileId) {
                if (deepResearchStore.sellerProfiles.length === 1) {
                  // Single profile → auto-pick (no choice to make).
                  deepResearchStore.setActiveSellerProfileId(
                    deepResearchStore.sellerProfiles[0].id
                  );
                } else {
                  // Multiple profiles → open the picker popup so the rep can
                  // actually choose inline. Previously we showed a red toaster
                  // telling them to pick — but they had no UI to pick from
                  // before clicking Run. The picker resolves that directly.
                  setShowRerunPopup(true);
                  return;
                }
              }
              startResearch();
            }}
            disabled={!linkedInUrl}
          >
            <i className="material-icons-outlined drIcon" style={{fontSize: "16px", marginRight: "6px"}}>biotech</i>
            {linkedInUrl ? "Start Deep Research" : "LinkedIn URL required"}
          </button>
        </div>
      </div>
    );
  }

  // ── Research state: two-panel layout ──
  return (
    <div className="deepResearchContainer">
      {showSellerPopup && (
        <SellerProfileSetup
          existingProfile={deepResearchStore.activeSellerProfile}
          onClose={() => setShowSellerPopup(false)}
          onSave={(profile) => {
            const editingId = deepResearchStore.activeSellerProfileId;
            const payload = editingId ? { ...profile, id: editingId } : profile;
            deepResearchStore.saveSellerProfile(payload);
            setShowSellerPopup(false);
          }}
        />
      )}
      {showRerunPopup && (
        <RerunResearchPopup
          profiles={deepResearchStore.sellerProfiles}
          initialSelectedId={deepResearchStore.activeSellerProfileId || ""}
          // First-time context (no research yet) → "Start research" framing.
          // Repeat context (research already loaded) → "Re-run" framing.
          title={researchStarted || hasData ? "Re-run deep research" : "Choose a seller profile"}
          runButtonLabel={researchStarted || hasData ? "Run research" : "Start research"}
          onClose={() => setShowRerunPopup(false)}
          onAddNew={() => {
            // Hand off to the existing setup flow; rep can come back to re-run
            // after creating the new profile.
            setShowRerunPopup(false);
            deepResearchStore.setActiveSellerProfileId(null);
            setShowSellerPopup(true);
          }}
          onRun={(sellerId) => {
            // Apply chosen profile, reset store + local research state, then
            // start. The auto-start useEffect picks this up because we set
            // researchStarted=false and hasData clears on store.reset().
            deepResearchStore.setActiveSellerProfileId(sellerId);
            deepResearchStore.reset();
            setResearchStarted(false);
            setLoadingList([]);
            setTimer(0);
            setShowRerunPopup(false);
            startResearch();
          }}
        />
      )}
      <SellerProfileBar
        profiles={deepResearchStore.sellerProfiles}
        activeProfileId={deepResearchStore.activeSellerProfileId}
        loadedResearchProfileId={deepResearchStore.loadedResearchSellerProfileId}
        onSelectProfile={(id) => deepResearchStore.setActiveSellerProfileId(id)}
        onAddNewProfile={() => {
          deepResearchStore.setActiveSellerProfileId(null);
          setShowSellerPopup(true);
        }}
        onEditProfile={() => setShowSellerPopup(true)}
      />
      <div className={`drTwoPanelInner ${hasData ? "" : "drTwoPanelInner--loading"}`}>
      {/* Left Panel — only AI chat (post-research). During loading, the left
          progress card is hidden entirely so the right panel can use the full
          width — the streaming progress UI on the right (ResearchingPlaceholder
          pinned at top + streaming source buckets) carries the loading story. */}
      {hasData && (
        <ChatPanel contactName={contactName} contactId={contactData?.contactId} />
      )}

      {/* Right Panel — SERP links + details */}
      <div className="drRightPanel">
        <div className="drRightHeader">
          <i className="material-icons-outlined drIcon" style={{fontSize: "16px"}}>biotech</i>
          <span className="drRightTitle">Deep Research</span>
          <span className="drRightSubtitle">{contactName}</span>
          {isComplete && <span className="drTimeBadge">Done in {timer.toFixed(1)}s</span>}
          {hasData && !timerActive && (
            <button
              className="drRerunBtn"
              onClick={() => setShowRerunPopup(true)}
            >
              🔄 Re-run research
            </button>
          )}
        </div>

        {/* Per-seller research history. Only renders when there are 2+ runs
            for this contact. Clicking a row switches the active seller — the
            overview-fetch useEffect picks that up and loads the right record. */}
        <ResearchHistoryStrip
          contactId={contactData?.contactId}
          activeSellerProfileId={deepResearchStore.activeSellerProfileId}
          onSelectSeller={(sellerId) => deepResearchStore.setActiveSellerProfileId(sellerId)}
          // Bumps when a fresh research completes (loaded seller id changes),
          // forcing the strip to re-fetch and pick up the new pill.
          refreshKey={deepResearchStore.loadedResearchSellerProfileId || ""}
        />

        {/* Sticky progress card — shows immediately on startResearch with
            rotating status lines. Stays pinned at the top of the right column
            and remains visible throughout the ENTIRE research (until full
            synthesis lands as `hasData`), not just until first SERP arrives.
            The streaming Sources panel below it populates incrementally. */}
        <div className="drResearchingSticky">
          <ResearchingPlaceholder
            contactName={contactName}
            sellerName={deepResearchStore.activeSellerProfile?.name
              || deepResearchStore.activeSellerProfile?.company_name
              || deepResearchStore.activeSellerProfile?.product_name}
            visible={isLoading && !hasData}
          />
        </div>

        {/* Full bucketed source explorer — populates incrementally from progress
            stream during initial DR, and renders from persisted serpLinks when
            the DR tab is re-opened later. Includes a highlighted "Tailored to
            Your Pitch" bucket with the dynamic-query rationale on top.
            isLive enables skeleton placeholders + live counter during research. */}
        <ResearchSourcesPanel
          serpLinks={deepResearchStore.serpLinks}
          crawlStatus={deepResearchStore.crawlStatus}
          dynamicQueries={deepResearchStore.dynamicQueries}
          dynamicReasoning={deepResearchStore.dynamicReasoning}
          isLive={isLoading && !isComplete}
        />

        {/* Synthesis-phase loader — big rotating-message card, not a dot strip */}
        {isLoading && !hasData && <InsightsLoader />}

        {/* Full Details */}
        {hasData && (
          <div className="drResults">
            {/* Fit hero + "why this contact" — pure-derive verdict shown
                first, so the rep gets the takeaway before the detail. */}
            <ContactHeroCard
              researchData={deepResearchStore.profileData}
              sellerName={deepResearchStore.activeSellerProfile?.name
                || deepResearchStore.activeSellerProfile?.company_name
                || deepResearchStore.activeSellerProfile?.product_name}
            />
            <WhyThisContact researchData={deepResearchStore.profileData} />

            {/* 1. Profile Summary */}
            <ProfileSummary
              profileData={deepResearchStore.profileData}
              structuredProfile={deepResearchStore.structuredProfile}
              dataQuality={deepResearchStore.dataQuality}
              serpLinks={deepResearchStore.serpLinks}
            />

            {/* 2. Career Timeline — surfaces position[] (was hidden). */}
            <CareerTimeline positions={deepResearchStore.profileData?.position} />

            {/* 2d. Best Channel — pure-derived outreach recommendation
                (no LLM). Reads notable_posts dates + deep dive signals. */}
            <BestChannel
              researchData={deepResearchStore.profileData}
              userInsights={deepResearchStore.insightsData}
            />

            {/* 3. Selling Signals — sits immediately after Best Channel so
                the rep reads the "why now" right after the "how to reach". */}
            {deepResearchStore.sellingSignals.length > 0 && (
              <SellingSignals
                signals={deepResearchStore.sellingSignals}
                serpLinks={deepResearchStore.serpLinks}
              />
            )}

            {/* 4. Conversation Starters */}
            {deepResearchStore.conversationStarters.length > 0 && (
              <ConversationStarters
                starters={deepResearchStore.conversationStarters}
                serpLinks={deepResearchStore.serpLinks}
              />
            )}

            {/* 4a. Warm Intros — AI-Ark people-search for manager + peers
                at the prospect's company. Sits below conversation starters
                as the multi-thread follow-on. */}
            <WarmIntros
              intros={deepResearchStore.profileData?.warm_intros}
              contactData={contactData}
              prospect={deepResearchStore.profileData}
            />

            {/* 4b. Deeper Context — notable_posts block. Sits below
                conversation_starters because reps read posts AFTER they've
                seen the openers (posts become reference material to
                paraphrase, not the lede). */}
            <DeeperContext userInsights={deepResearchStore.insightsData} />

            {/* 5. Key Mentions */}
            <KeyMentions
              mentions={deepResearchStore.insightsData?.key_mentions}
              serpLinks={deepResearchStore.serpLinks}
            />

            {/* 6. Company Overview + Insights — competitors, tools, facts.
                Sits at the account-section boundary; org chart follows it. */}
            <CompanyOverview
              companyData={deepResearchStore.companyData}
              companyInsights={deepResearchStore.companyInsights}
              deepDiveIntel={deepResearchStore.deepDiveIntel}
              serpLinks={deepResearchStore.serpLinks}
              accountId={contactData?.accountId}
            />

            {/* 7. Org Chart — tiered flowchart parsed from theorg.com SERP
                (with Jina fallback) and cached at the account level. Sits
                at the bottom as the final account-side context block. */}
            <OrgChart orgChart={deepResearchStore.profileData?.org_chart} />
          </div>
        )}
      </div>
      </div>
    </div>
  );
});

export default DeepResearch;
